import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { MeetingSessionHighlightingOnResponseActionHandler, MeetingSessionHighlightingState, SocketActionHandler } from "@kalyzee/kast-web-sdk/websocket";
import { useEffect, useMemo, useRef } from "react";
import { useMeetingUtilsContext } from "../contexts/meetingUtils/context";
import { isExpert } from "../helpers/roles";
import { HighlightedSessions } from "../interfaces/highlight";
import { toastError, toastInfo, toastSuccess } from "../helpers/toast";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useSdkContext } from "../contexts/sdk/context";
import { addPopup, removePopup } from "../helpers/popup";
import { BoxOverlay, OverlayContainer } from "@kalyzee/kalyzee-ui";

export const useHighlightSessions = (params?: { onNewRequests?: () => void }): HighlightedSessions => {
  const { populated, containerAPI } = useMeetingUtilsContext();
  const [sessionId] = useMeetingSelector(containerAPI, "session", s => s?.id);

  const seenRequests = useRef(new Set<string>());

  const confirmedSessionsSet = useMemo(() => {
    const set = new Set<string>();
    populated.activity?.confirmedSessions?.forEach(s => set.add(s.id));
    return set;
  }, [populated.activity?.confirmedSessions]);

  const sessions = useMemo(() => {
    const output: HighlightedSessions = {
      ongoing: [],
      requesting: [],
      inactive: [],
    };

    populated.users?.forEach((pUser) => {
      pUser.sessions?.forEach((incSession) => {
        if (isExpert(incSession.session)) return;
        if (incSession.id === sessionId) return;
        switch (incSession.session.highlighting) {
          case MeetingSessionHighlightingState.PENDING_REQUEST:
            output.requesting.push(incSession);
            break;
          case MeetingSessionHighlightingState.ONGOING:
            const pending = !confirmedSessionsSet.has(incSession.id);
            output.ongoing.push({ session: incSession, pending });
            break;
          default:
            output.inactive.push(incSession);
        }
      });
    });

    return output;
  }, [populated.users, confirmedSessionsSet, sessionId]);

  useEffect(() => {
    if (!params?.onNewRequests) return;

    const newRequests = new Set<string>();
    sessions.requesting.forEach(s => {
      newRequests.add(s.id);
    });

    // check if there is some requests that were not in the previous seen
    
    let hasNewRequests = false;
    newRequests.forEach(id => {
      if (!seenRequests.current.has(id)) hasNewRequests = true;
    });
    if (hasNewRequests) params.onNewRequests();

    seenRequests.current = newRequests;
    
  }, [sessions.requesting]);

  return sessions;
};

export const useHighlightNotifications = () => {
  const { populated, containerAPI } = useMeetingUtilsContext();
  const [session] = useMeetingSelector(containerAPI, "session");
  const [meetingId] = useMeetingSelector(containerAPI, "meeting", meeting => meeting?.id);  

  const sdkCtx = useSdkContext();
  const sdkCtxRef = useRefAutoUpdate(sdkCtx);

  const selfActivity = useMemo(() => {
    if (!session) return undefined;

    // No ongoing activity
    if (!populated.activity) return undefined;

    // I am not concerned by the activity
    if (!populated.activity.sessions.some(s => s.id === session.id)) return undefined;

    return {
      // I confirmed my participation
      activity: populated.activity,
      confirmed: populated.activity.confirmedSessions.some(s => s.id === session.id),
    };
  }, [populated.activity, session]);
  
  const iAmRequestingRef = useRef(session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST);
  const onBoardRef = useRef(selfActivity?.confirmed ?? false);

  ////////////////// UTILS //////////////////

  const onJoinActivity = async () => {
    if (!meetingId) return;
    const client = sdkCtxRef.current.client.getWebsocketClient();
    const response = await client.emitter.meeting.enlistInActivityRoom({
      id: meetingId,
    });
    if (response.error || !response.response) {
      toastError("Échec de l'intervention");
      return;
    }
  };

  const onRemoveHighlight = async () => {
    if (!session) return;
    const client = sdkCtxRef.current.client.getWebsocketClient();
    const response = await client.emitter.meeting.stopHighlighting({
      sessionId: session.id,
    });
    if (response.error || !response.response) {
      toastError("Une erreur est survenue");
      return;
    }
  };

  ////////////////// HOOKS //////////////////

  useEffect(() => {
    if (session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) {
      iAmRequestingRef.current = true;
    }
  }, [session?.highlighting]);

  useEffect(() => {
    if (selfActivity?.confirmed) {
      if (iAmRequestingRef.current) {
        toastSuccess("Votre demande d'intervention a été acceptée");
      } else {
        toastSuccess("Confirmation de votre intervention");
      }
      onBoardRef.current = true;
    } else {
      if (onBoardRef.current) {
        toastInfo("Fin de votre intervention");
        onBoardRef.current = false;
      }
    }
    iAmRequestingRef.current = false;
  }, [selfActivity?.confirmed]);

  useEffect(() => {
    const client = sdkCtxRef.current.client.getWebsocketClient();

    const handlers: SocketActionHandler[] = [];

    const responseHandler = new MeetingSessionHighlightingOnResponseActionHandler();
    responseHandler.onReceive = (event) => {
      if (!event.content.accepted) {
        toastInfo("Votre demande d'intervention a été refusée");
      }
    }
    handlers.push(responseHandler);

    client.registerActionHandlers(handlers);
    return () => client.unregisterActionHandlers(handlers);
  }, []);

  useEffect(() => {
    if (!selfActivity || selfActivity.confirmed) return;

    let currentPopup: React.ReactNode;
    const joinActivityPopup = () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }

      let buttons = [
        {
          label: "Accepter et intervenir",
          onClick: onJoinActivity,
          validateAfter: 10,
        },
        {
          label: "Refuser",
          onClick: onRemoveHighlight,
        },
      ];

      currentPopup = (
        <OverlayContainer
          style={{
            width: "100vw",
            height: "100vh",
          }}
          overlay={
            <BoxOverlay
              title={"Vous avez été invité à intervenir"}
              level={"info"}
              buttons={buttons}
            />
          }
        />
      );
      
      addPopup(currentPopup, "meeting_activity_invitation", "main");
    };

    joinActivityPopup();

    return () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [selfActivity?.confirmed]);
};
